import { Box, Collapse, TextField, TextareaAutosize, Tooltip } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import React, { Dispatch, FC, SetStateAction, useEffect } from "react";
import { Control, Controller, UseFormSetValue, useForm } from "react-hook-form";
import { FieldState, translateRules, useStyles } from "../common";
import SharedFieldForm from "./SharedFieldForm";
import { FIELD_ACTIVITY_STATUS, FIELD_PLAYCARD_STATUS, FIELD_TASK_STATUS } from "constant";

interface OptionType {
  label: string;
  value: string;
}

const InputOptions: OptionType[] = [
  {
    label: "Text Input",
    value: "text-input",
  },
  {
    label: "Text Area",
    value: "text-area",
  },
];

interface TextFieldProps {
  state: FieldState | undefined;
  control: Control;
  setValue: UseFormSetValue<any>;
  setFieldStatelist: React.Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
  readOnly?: boolean;
  handleInput: (
    value: string,
    setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>
  ) => void;
  enableValidate: boolean;
  showLimit?: boolean;
}

const InputTextField: FC<TextFieldProps> = ({
  state,
  control,
  setFieldStatelist,
  readOnly = false,
  handleInput,
  enableValidate = true,
}) => {
  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <>
          <TextField
            {...field}
            fullWidth={true}
            value={field.value}
            inputProps={{
              readOnly: readOnly,
            }}
            autoFocus={false}
            variant="outlined"
            size="small"
            style={{ background: "#fff", marginTop: 8 }}
            placeholder={state?.placeholder.value}
            onChange={(e: React.ChangeEvent<any>) => handleInput(e.target.value, setFieldStatelist)}
          />
          <div style={{ color: "#f44336", marginTop: "4px" }}>
            {fieldState.error ? (
              <span style={{ color: "#f44336", marginTop: "4px" }}>{fieldState.error.message}</span>
            ) : null}
          </div>
        </>
      )}
      name={state?.id ?? ""}
      defaultValue={state?.defaultValue ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const AreaTextField: FC<TextFieldProps> = ({
  state,
  control,
  setFieldStatelist,
  readOnly = false,
  handleInput,
  enableValidate = true,
  showLimit = false,
}) => {
  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <>
          <div
            style={{
              border: "1px solid gray",
              borderRadius: 4,
              backgroundColor: "rgb(255, 255, 255)",
              marginTop: 8,
            }}
          >
            <TextareaAutosize
              {...field}
              minRows={3}
              maxRows={4}
              autoFocus={false}
              readOnly={readOnly}
              value={field.value}
              style={{
                background: "#fff",
                borderRadius: 4,
                width: "100%",
                resize: "none",
                padding: 10,
                border: 0,
              }}
              placeholder={state?.placeholder.value}
              onChange={(e: React.ChangeEvent<any>) =>
                handleInput(e.target.value, setFieldStatelist)
              }
            />
            {showLimit ? (
              <div
                style={{
                  textAlign: "right",
                  fontSize: 12,
                  color: "rgb(137, 137, 137)",
                  marginRight: 5,
                }}
              >
                <span>{`${state?.defaultValue.length} / ${state?.rules.maxLength.value}`}</span>
              </div>
            ) : undefined}
          </div>
          <div style={{ color: "#f44336", marginTop: "4px" }}>
            {fieldState.error ? (
              <span style={{ color: "#f44336", marginTop: "4px" }}>{fieldState.error.message}</span>
            ) : null}
          </div>
        </>
      )}
      name={state?.id ?? ""}
      defaultValue={state?.defaultValue ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const InputTextWrapper: FC<{
  className: any;
  fieldId: string;
  state?: FieldState;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  readOnly: boolean;
  setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
}> = ({
  className,
  fieldId,
  state,
  control,
  setValue,
  enableValidate,
  readOnly,
  setFieldStateList,
  disabled = false,
}) => {
  const fieldSubType =
    state?.fieldSubType === "" || state?.fieldSubType === undefined
      ? "text-input"
      : state?.fieldSubType;

  const updateInputValue = (
    value: string,
    setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>
  ) => {
    setFieldStateList((items) =>
      items.map((item) => (item.id === fieldId ? { ...item, defaultValue: value } : item))
    );
    setValue(fieldId, value, { shouldValidate: true });
  };

  return (
    <div className={className}>
      <p
        style={{
          fontSize: 12,
          color: "#535759",
          fontWeight: 500,
          display: "flex",
        }}
      >
        {state?.rules.required?.value === "y" && (
          <span style={{ color: "#FF5F58", marginRight: 4 }}>*</span>
        )}
        {state?.label.value === "" ? "Label Name" : state?.label.value}
      </p>
      {fieldSubType === "text-input" ? (
        <InputTextField
          state={state}
          setValue={setValue}
          control={control}
          setFieldStatelist={setFieldStateList}
          readOnly={readOnly}
          handleInput={updateInputValue}
          enableValidate={enableValidate}
          disabled={disabled}
        />
      ) : (
        <AreaTextField
          state={state}
          setValue={setValue}
          control={control}
          setFieldStatelist={setFieldStateList}
          readOnly={readOnly}
          handleInput={updateInputValue}
          enableValidate={enableValidate}
          showLimit={true}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export const ConfigInputTextComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  disabled?: boolean;
}> = ({ fieldId, control, setValue, enableValidate }) => {
  const classes = useStyles();

  const { setFieldStateList, selectField, removeField, isCurrentField, getFieldState } =
    useFieldCustomize();

  const state = getFieldState(fieldId);

  return (
    <Tooltip
      title={
        <DeleteOutline
          className={classes.trashIcon}
          onClick={() => removeField(fieldId)}
        />
      }
      placement={"right-start"}
      classes={{ tooltip: classes.tooltip }}
    >
      <div onClick={() => selectField(fieldId)}>
        <InputTextWrapper
          className={isCurrentField(fieldId) ? classes.selectedField : classes.unselectedInnerField}
          fieldId={fieldId}
          state={state}
          control={control}
          setValue={setValue}
          enableValidate={enableValidate}
          readOnly={true}
          setFieldStateList={setFieldStateList}
        />
      </div>
    </Tooltip>
  );
};

export const PlainInputTextComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  stateList: FieldState[];
  setStateList: Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
}> = ({
  fieldId,
  control,
  setValue,
  enableValidate,
  disabled = false,
  stateList,
  setStateList,
}) => {
  const classes = useStyles();

  /*   const { finalStateList, setFinalStateList } = useContext(
    CustomFieldOuterContext
  ); */

  const state = stateList.find((x) => x.id === fieldId);

  const notMapID = [FIELD_ACTIVITY_STATUS, FIELD_TASK_STATUS, FIELD_PLAYCARD_STATUS];

  useEffect(() => {
    if (state && (state?.visible ?? true)) {
      setValue(fieldId, state?.defaultValue, { shouldValidate: true });
    }
  }, [state]);

  useEffect(() => {
    if (!notMapID.includes(state?.id as string)) {
      if (!(state?.visible ?? true)) {
        setStateList((items) =>
          items.map((item) =>
            item.id === state?.id
              ? {
                  ...item,
                  defaultValue: state.isMultiple ? [] : "",
                }
              : item
          )
        );

        (state?.dependsBy ?? []).map((fieldId) =>
          setStateList((items) =>
            items.map((item) =>
              item.id === fieldId
                ? {
                    ...item,
                    defaultValue: item.isMultiple ? [] : "",
                    visible: false,
                  }
                : item
            )
          )
        );
      }
    }
  }, [state?.visible]);

  return (
    <Collapse
      in={state?.visible ?? true}
      style={{
        width: "100%",
        height: (state?.visible ?? true) ? "auto" : 0,
        overflow: "hidden",
      }}
    >
      <InputTextWrapper
        className={classes.unselectedField}
        fieldId={fieldId}
        state={state}
        control={control}
        setValue={setValue}
        enableValidate={enableValidate}
        readOnly={false}
        setFieldStateList={setStateList}
        disabled={disabled}
      />
    </Collapse>
  );
};

export const InputTypeSelector: FC<{
  state?: FieldState;
  setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>;
}> = ({ state, setFieldStateList }) => {
  const fieldId = state?.id ?? "";

  const option = InputOptions.find((o) => o.value === state?.fieldSubType) ?? InputOptions[0];

  const handleTypeChange = (value: OptionType | null) => {
    const o = value ?? InputOptions[0];
    setFieldStateList((items) =>
      items.map((item) => (item.id === fieldId ? { ...item, fieldSubType: o.value } : item))
    );
  };

  return (
    <Box>
      <p
        style={{
          fontSize: 12,
          color: "#535759",
          fontWeight: 500,
          display: "flex",
        }}
      >
        Text Input Type
      </p>
      <Autocomplete
        multiple={false}
        size="small"
        options={InputOptions}
        value={option}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        getOptionLabel={(o) => o.label || ""}
        renderInput={(params) => {
          const p = {
            ...params,
            inputProps: {
              ...params.inputProps,
              readOnly: true,
            },
          };
          return (
            <TextField
              {...p}
              margin="normal"
              variant="outlined"
              size={"small"}
              style={{ backgroundColor: "#fff", marginTop: 5 }}
              autoFocus={false}
            />
          );
        }}
        onChange={(e, value) => handleTypeChange(value)}
      />
    </Box>
  );
};

export const InputTextPropertyForm: FC<{ fieldId: string }> = ({ fieldId }) => {
  const { setFieldStateList, getFieldState } = useFieldCustomize();
  const state = getFieldState(fieldId);

  const { control, handleSubmit, setValue, unregister } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    // empty funciton
    setFieldStateList((items) =>
      items.map((item) => (item.id === state?.id ? { ...item, passValidate: true } : item))
    );
  };

  const onError = () => {
    // empty function
  };

  useEffect(() => {
    setValue("labelName", state?.label.value);
    setValue("maxLength", state?.rules.maxLength.value);
    state?.conditions?.map((c) => {
      setValue(c.ruleId, c.fieldId);
      setValue(`${c.ruleId}-values`, c.fieldValue);
    });
    handleSubmit(onSubmit, onError)();
  }, [state?.conditions, state?.label, state?.rules]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <SharedFieldForm
        formHeader="Text Input"
        fieldId={fieldId}
        control={control}
        setValue={setValue}
        unregister={unregister}
        specComponent={
          <InputTypeSelector
            state={state}
            setFieldStateList={setFieldStateList}
          />
        }
      />
    </form>
  );
};

export const InputTextViewComponent: FC<{ state: FieldState }> = ({ state }) => {
  const classes = useStyles();

  const subType = state.fieldSubType ?? "";
  const fieldSubType = subType === "" || subType === undefined ? "text-input" : subType;
  return (
    <>
      {(state?.visible ?? true) && fieldSubType === "text-input" && (
        <div
          key={state.id}
          style={{
            width: "100%",
            lineHeight: "32px",
            marginTop: 8,
          }}
        >
          <div style={{ display: "block", width: "100%", marginLeft: 4 }}>
            <span className={classes.field}>
              {state.label.value ? state.label.value : "Label Name"}:
            </span>
          </div>
          <div
            style={{
              backgroundColor: "#F7F7F7",
              opacity: 0.8,
              borderRadius: 4,
              padding: 5,
            }}
          >
            <span>{state.defaultValue}</span>
          </div>
        </div>
      )}
      {(state?.visible ?? true) && fieldSubType === "text-area" && (
        <div
          key={state.id}
          style={{ width: "100%", marginTop: 8 }}
        >
          <div style={{ display: "block", width: "100%", marginLeft: 4 }}>
            <span className={classes.field}>
              {state.label.value ? state.label.value : "Label Name"}:
            </span>
          </div>
          <div
            style={{
              whiteSpace: "pre-wrap",
              overflowY: "auto",
              height: "100px",
              margin: 4,
              backgroundColor: "#F7F7F7",
              opacity: 0.8,
              borderRadius: 4,
              padding: 5,
            }}
          >
            {state.defaultValue}
          </div>
        </div>
      )}
    </>
  );
};
